<template>
  <div class="waajo-ticket-detail-panel">
    <div v-if="loading" class="waajo-ticket-detail-loading">
      <span class="waajo-ticket-sk is-title"></span>
      <span class="waajo-ticket-sk"></span>
      <span class="waajo-ticket-sk is-short"></span>
    </div>

    <div v-else-if="error" class="waajo-ticket-state is-error">
      <i class="fa fa-exclamation-circle"></i>
      <p>{{ error }}</p>
      <button type="button" class="waajo-ticket-btn is-primary" @click="fetchDetail">Coba Lagi</button>
    </div>

    <template v-else-if="detail">
      <div class="waajo-ticket-detail-head">
        <div class="waajo-ticket-detail-head-copy">
          <span class="waajo-ticket-eyebrow"><i class="fa fa-ticket"></i> {{ detail.ticket_number }}</span>
          <h3 class="waajo-ticket-detail-title">{{ detail.title }}</h3>
          <div class="waajo-ticket-detail-badges">
            <span class="waajo-ticket-badge" :class="'is-status-' + detail.status.replace('_', '-')">{{ statusLabel(detail.status) }}</span>
            <span class="waajo-ticket-badge" :class="'is-priority-' + detail.priority">{{ priorityLabel(detail.priority) }}</span>
            <span class="waajo-ticket-detail-category">{{ categoryLabel(detail.category) }}</span>
          </div>
        </div>
        <div class="waajo-ticket-detail-head-actions">
          <button type="button" class="waajo-ticket-btn" :disabled="chatOpening" @click="openChat">
            <i v-if="chatOpening" class="fa fa-spinner fa-spin"></i>
            <i v-else class="fa fa-comments"></i>
            {{ chatOpening ? 'Membuka...' : 'Lihat Chat di Omni' }}
          </button>
          <button
            v-if="showClose"
            type="button"
            class="waajo-ticket-detail-close"
            title="Tutup detail"
            @click="$emit('close')"
          >
            <i class="fa fa-times"></i>
          </button>
        </div>
      </div>

            <div v-if="actionError" class="waajo-ticket-form-error">
              <i class="fa fa-exclamation-circle"></i> {{ actionError }}
            </div>
            <div v-if="actionSuccess" class="waajo-ticket-form-success">
              <i class="fa fa-check-circle"></i> {{ actionSuccess }}
            </div>

            <div class="waajo-ticket-detail-grid">
              <div class="waajo-ticket-detail-main">
                <section class="waajo-ticket-panel">
                  <div class="waajo-ticket-panel-head">
                    <h4><i class="fa fa-info-circle"></i> Informasi Tiket</h4>
                    <button v-if="!editMode" type="button" class="waajo-ticket-link-btn" @click="startEdit">
                      <i class="fa fa-pencil"></i> Ubah
                    </button>
                  </div>

                  <div v-if="editMode" class="waajo-ticket-edit-form">
                    <div class="waajo-ticket-form-group">
                      <label>Judul Tiket</label>
                      <input v-model.trim="editForm.title" type="text" maxlength="255" :disabled="savingEdit">
                    </div>
                    <div class="waajo-ticket-form-grid">
                      <div class="waajo-ticket-form-group">
                        <label>Kategori</label>
                        <select v-model="editForm.category" :disabled="savingEdit">
                          <option v-for="item in categories" :key="item.value" :value="item.value">{{ item.label }}</option>
                        </select>
                      </div>
                      <div class="waajo-ticket-form-group">
                        <label>Prioritas</label>
                        <select v-model="editForm.priority" :disabled="savingEdit">
                          <option v-for="item in priorities" :key="item.value" :value="item.value">{{ item.label }}</option>
                        </select>
                      </div>
                    </div>
                    <div class="waajo-ticket-form-group">
                      <label>Deskripsi</label>
                      <textarea v-model.trim="editForm.description" rows="6" maxlength="10000" :disabled="savingEdit"></textarea>
                    </div>
                    <div class="waajo-ticket-inline-actions">
                      <button type="button" class="waajo-ticket-btn" :disabled="savingEdit" @click="cancelEdit">Batal</button>
                      <button type="button" class="waajo-ticket-btn is-primary" :disabled="savingEdit" @click="saveEdit">
                        <i v-if="savingEdit" class="fa fa-spinner fa-spin"></i>
                        {{ savingEdit ? 'Menyimpan...' : 'Simpan Perubahan' }}
                      </button>
                    </div>
                  </div>

                  <template v-else>
                    <p class="waajo-ticket-description">{{ detail.description }}</p>
                    <div class="waajo-ticket-meta-grid">
                      <div>
                        <span>Status</span>
                        <strong>{{ statusLabel(detail.status) }}</strong>
                      </div>
                      <div>
                        <span>Prioritas</span>
                        <strong>{{ priorityLabel(detail.priority) }}</strong>
                      </div>
                      <div>
                        <span>Kategori</span>
                        <strong>{{ categoryLabel(detail.category) }}</strong>
                      </div>
                      <div>
                        <span>PIC</span>
                        <strong>{{ detail.assigned_agent_name || 'Belum ditentukan' }}</strong>
                      </div>
                      <div>
                        <span>Jadwal Tindak Lanjut</span>
                        <strong>{{ formatTicketDateTime(detail.follow_up_datetime) }}</strong>
                      </div>
                      <div>
                        <span>Dibuat</span>
                        <strong>{{ formatTicketDateTime(detail.create_datetime) }}</strong>
                      </div>
                    </div>
                  </template>
                </section>

                <ticket-attachments
                  :ticket-id="detail.id"
                  @ticket-missing="fetchDetail"
                />

                <section class="waajo-ticket-panel">
                  <div class="waajo-ticket-panel-head">
                    <h4><i class="fa fa-history"></i> Riwayat Aktivitas</h4>
                    <span class="waajo-ticket-panel-count">{{ activityCount }} aktivitas</span>
                  </div>
                  <div v-if="!detail.activities || !detail.activities.length" class="waajo-ticket-timeline-empty">
                    Belum ada aktivitas.
                  </div>
                  <div v-else class="waajo-ticket-timeline">
                    <div v-for="activity in detail.activities" :key="activity.id" class="waajo-ticket-timeline-item">
                      <span class="waajo-ticket-timeline-icon" :class="'is-' + activity.activity_type">
                        <i :class="activityIcon(activity.activity_type)"></i>
                      </span>
                      <div class="waajo-ticket-timeline-body">
                        <div class="waajo-ticket-timeline-top">
                          <strong>{{ activityLabel(activity.activity_type) }}</strong>
                          <small>{{ formatTicketDateTime(activity.create_datetime) }}</small>
                        </div>
                        <p :class="{ 'is-note': activity.activity_type === 'internal_note' }">{{ activity.description }}</p>
                        <span class="waajo-ticket-timeline-actor">
                          <i class="fa fa-user"></i>
                          {{ activity.created_by_name || 'Sistem' }}
                          <template v-if="activity.created_by_type === 'agent'"> · Agent</template>
                        </span>
                      </div>
                    </div>
                  </div>
                </section>
              </div>

              <aside class="waajo-ticket-detail-side">
                <section class="waajo-ticket-panel">
                  <div class="waajo-ticket-panel-head">
                    <h4><i class="fa fa-user"></i> Informasi Pelanggan</h4>
                  </div>
                  <div class="waajo-ticket-customer-card">
                    <div class="waajo-ticket-customer-avatar">
                      {{ customerInitial }}
                    </div>
                    <div>
                      <strong>{{ detail.contact_name || '-' }}</strong>
                      <span>{{ detail.contact_number }}</span>
                      <small v-if="detail.contact_id" class="waajo-ticket-master-badge">Kontak master</small>
                    </div>
                  </div>
                  <div class="waajo-ticket-info-list">
                    <div>
                      <span>Nomor WhatsApp CS</span>
                      <strong>{{ formatTicketPhone(detail.license_phone_number, detail.license_name || '-') }}</strong>
                      <small v-if="detail.license_name && detail.license_phone_number">{{ detail.license_name }}</small>
                    </div>
                    <div>
                      <span>Room</span>
                      <strong>{{ detail.room_title || detail.room_id || '-' }}</strong>
                    </div>
                    <div>
                      <span>Dibuat oleh</span>
                      <strong>{{ detail.created_by_name || '-' }}</strong>
                      <small v-if="detail.created_by_type === 'agent'">Agent</small>
                    </div>
                  </div>
                  <button type="button" class="waajo-ticket-btn is-primary is-block" :disabled="chatOpening" @click="openChat">
                    <i class="fa fa-comments"></i> Lihat Chat di Omni
                  </button>
                </section>

                <ticket-follow-up-panel
                  :ticket="detail"
                  @scheduled="fetchDetail"
                  @cancelled="fetchDetail"
                />

                <section class="waajo-ticket-panel">
                  <div class="waajo-ticket-panel-head">
                    <h4><i class="fa fa-sliders"></i> Tindakan</h4>
                  </div>
                  <div class="waajo-ticket-action-block">
                    <label>Status Tiket</label>
                    <div class="waajo-ticket-inline-form">
                      <select v-model="statusForm.status" :disabled="savingStatus">
                        <option v-for="item in statuses" :key="item.value" :value="item.value">{{ item.label }}</option>
                      </select>
                      <button type="button" class="waajo-ticket-btn is-primary" :disabled="savingStatus || statusForm.status === detail.status" @click="saveStatus">
                        <i v-if="savingStatus" class="fa fa-spinner fa-spin"></i>
                        Simpan
                      </button>
                    </div>
                    <small v-if="detail.status === 'resolved'">Pilih Open untuk membuka kembali tiket.</small>
                  </div>

                  <div class="waajo-ticket-action-block">
                    <label>Prioritas</label>
                    <select v-model="infoForm.priority" :disabled="savingInfo">
                      <option v-for="item in priorities" :key="item.value" :value="item.value">{{ item.label }}</option>
                    </select>
                    <label class="waajo-ticket-action-label">Jadwal Tindak Lanjut</label>
                    <input v-model="infoForm.follow_up_local" type="datetime-local" :disabled="savingInfo">
                    <button type="button" class="waajo-ticket-btn is-primary is-block" :disabled="savingInfo" @click="saveInfo">
                      <i v-if="savingInfo" class="fa fa-spinner fa-spin"></i>
                      Simpan Prioritas &amp; Jadwal
                    </button>
                  </div>

                  <div class="waajo-ticket-action-block">
                    <label>PIC / Agent</label>
                    <select v-model="assignForm.assigned_agent_id" :disabled="savingAgent || loadingAgents">
                      <option value="">Belum ditentukan</option>
                      <option v-for="agent in agentOptions" :key="agent.id" :value="agent.id" :disabled="agent.is_assignable === false">
                        {{ agent.name }}<template v-if="agent.active_ticket_count"> — {{ agent.active_ticket_count }} tiket aktif</template>
                      </option>
                    </select>
                    <small v-if="loadingAgents"><i class="fa fa-spinner fa-spin"></i> Memuat Agent...</small>
                    <small v-else-if="!agentOptions.length">Tidak ada Agent aktif pada lisensi ini.</small>
                    <button type="button" class="waajo-ticket-btn is-primary is-block" :disabled="savingAgent || assignForm.assigned_agent_id === detail.assigned_agent_id" @click="saveAgent">
                      <i v-if="savingAgent" class="fa fa-spinner fa-spin"></i>
                      Simpan PIC
                    </button>
                  </div>

                  <div class="waajo-ticket-action-block">
                    <label>Catatan Internal</label>
                    <textarea v-model.trim="noteForm.note" rows="4" maxlength="5000" placeholder="Catatan ini tidak dikirim ke pelanggan" :disabled="savingNote"></textarea>
                    <button type="button" class="waajo-ticket-btn is-primary is-block" :disabled="savingNote || !noteForm.note" @click="saveNote">
                      <i v-if="savingNote" class="fa fa-spinner fa-spin"></i>
                      Simpan Catatan
                    </button>
                  </div>
                </section>
              </aside>
            </div>
      </template>
  </div>
</template>

<script>
import { openChatRoomForSession } from '@/utils/chatRoom'
import TicketFollowUpPanel from './TicketFollowUpPanel.vue'
import TicketAttachments from './TicketAttachments.vue'
import {
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
  ticketStatusLabel,
  ticketPriorityLabel,
  ticketCategoryLabel,
  formatTicketDateTime,
  formatTicketPhone,
  toDatetimeLocal,
  datetimeLocalToUnix,
  ticketActivityLabel,
  ticketActivityIcon,
  extractApiErrorMessage
} from '@/utils/ticket'

export default {
  name: 'TicketDetailPanel',
  components: {
    TicketFollowUpPanel,
    TicketAttachments
  },
  props: {
    ticketId: {
      type: [String, Number],
      default: ''
    },
    showClose: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      categories: TICKET_CATEGORIES,
      priorities: TICKET_PRIORITIES,
      statuses: TICKET_STATUSES,
      detail: null,
      agents: [],
      loading: true,
      loadingAgents: false,
      error: '',
      actionError: '',
      actionSuccess: '',
      editMode: false,
      chatOpening: false,
      savingEdit: false,
      savingStatus: false,
      savingInfo: false,
      savingAgent: false,
      savingNote: false,
      editForm: {
        title: '',
        description: '',
        category: '',
        priority: 'normal'
      },
      statusForm: {
        status: 'open'
      },
      infoForm: {
        priority: 'normal',
        follow_up_local: ''
      },
      assignForm: {
        assigned_agent_id: ''
      },
      noteForm: {
        note: ''
      }
    }
  },
  computed: {
    activityCount() {
      return this.detail && Array.isArray(this.detail.activities) ? this.detail.activities.length : 0
    },
    customerInitial() {
      const name = this.detail && this.detail.contact_name ? this.detail.contact_name.trim() : ''
      return name ? name.charAt(0).toUpperCase() : '#'
    },
    agentOptions() {
      const list = Array.isArray(this.agents) ? this.agents.slice() : []
      if (
        this.detail &&
        this.detail.assigned_agent_id &&
        !list.some(agent => agent.id === this.detail.assigned_agent_id)
      ) {
        list.unshift({
          id: this.detail.assigned_agent_id,
          name: (this.detail.assigned_agent_name || this.detail.assigned_agent_id) + ' (tidak aktif)',
          email: this.detail.assigned_agent_email || '',
          is_assignable: false,
          active_ticket_count: 0
        })
      }
      return list
    }
  },
  created() {
    this.fetchDetail()
  },
  watch: {
    ticketId(value, oldValue) {
      if (!value || String(value) === String(oldValue)) return
      this.fetchDetail()
    }
  },
  methods: {
    statusLabel: ticketStatusLabel,
    priorityLabel: ticketPriorityLabel,
    categoryLabel: ticketCategoryLabel,
    formatTicketDateTime,
    formatTicketPhone,
    activityLabel: ticketActivityLabel,
    activityIcon: ticketActivityIcon,
    reload() {
      return this.fetchDetail()
    },
    async fetchDetail() {
      if (!this.ticketId) {
        this.detail = null
        this.loading = false
        this.error = 'ID tiket tidak valid'
        return
      }
      this.loading = true
      this.error = ''
      try {
        const response = await this.$store.dispatch('ticket/detail', { id: this.ticketId })
        this.applyDetail(response.data)
        this.$emit('loaded', response.data)
        this.fetchAgents()
      } catch (error) {
        this.detail = null
        this.error = extractApiErrorMessage(error, 'Gagal memuat detail tiket')
      } finally {
        this.loading = false
      }
    },
    async fetchAgents() {
      if (!this.detail || !this.detail.license_id) return
      this.loadingAgents = true
      try {
        const response = await this.$store.dispatch('ticket/agents', { license_id: this.detail.license_id })
        this.agents = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        this.agents = []
      } finally {
        this.loadingAgents = false
      }
    },
    applyDetail(payload) {
      this.detail = payload
      this.statusForm.status = payload.status
      this.infoForm.priority = payload.priority
      this.infoForm.follow_up_local = toDatetimeLocal(payload.follow_up_datetime)
      this.assignForm.assigned_agent_id = payload.assigned_agent_id || ''
      this.editForm.title = payload.title
      this.editForm.description = payload.description
      this.editForm.category = payload.category
      this.editForm.priority = payload.priority
    },
    startEdit() {
      this.editMode = true
      this.actionError = ''
      this.actionSuccess = ''
      this.editForm.title = this.detail.title
      this.editForm.description = this.detail.description
      this.editForm.category = this.detail.category
      this.editForm.priority = this.detail.priority
    },
    cancelEdit() {
      this.editMode = false
      this.actionError = ''
    },
    async saveEdit() {
      if (this.savingEdit) return
      if (!this.editForm.title || !this.editForm.description || !this.editForm.category) {
        this.actionError = 'Judul, kategori, dan deskripsi wajib diisi.'
        return
      }
      this.savingEdit = true
      this.clearMessages()
      try {
        const response = await this.$store.dispatch('ticket/update', {
          id: this.detail.id,
          title: this.editForm.title,
          description: this.editForm.description,
          category: this.editForm.category,
          priority: this.editForm.priority,
          follow_up_datetime: this.detail.follow_up_datetime
        })
        this.applyDetail(response.data)
        this.$emit('updated', response.data)
        this.editMode = false
        this.showSuccess('Informasi tiket berhasil diperbarui.')
      } catch (error) {
        this.actionError = extractApiErrorMessage(error, 'Gagal memperbarui tiket')
      } finally {
        this.savingEdit = false
      }
    },
    async saveStatus() {
      if (this.savingStatus) return
      const nextStatus = this.statusForm.status
      if (nextStatus === this.detail.status) return
      this.savingStatus = true
      this.clearMessages()
      try {
        const response = await this.$store.dispatch('ticket/updateStatus', {
          id: this.detail.id,
          status: nextStatus
        })
        this.applyDetail(response.data)
        this.$emit('updated', response.data)
        this.showSuccess('Status tiket berhasil diperbarui.')
      } catch (error) {
        this.actionError = extractApiErrorMessage(error, 'Gagal mengubah status tiket')
        this.statusForm.status = this.detail.status
      } finally {
        this.savingStatus = false
      }
    },
    async saveInfo() {
      if (this.savingInfo) return
      const followUpDatetime = datetimeLocalToUnix(this.infoForm.follow_up_local)
      if (this.infoForm.follow_up_local && !followUpDatetime) {
        this.actionError = 'Jadwal tindak lanjut tidak valid.'
        return
      }
      this.savingInfo = true
      this.clearMessages()
      try {
        const response = await this.$store.dispatch('ticket/update', {
          id: this.detail.id,
          title: this.detail.title,
          description: this.detail.description,
          category: this.detail.category,
          priority: this.infoForm.priority,
          follow_up_datetime: followUpDatetime
        })
        this.applyDetail(response.data)
        this.$emit('updated', response.data)
        this.showSuccess('Prioritas dan jadwal berhasil diperbarui.')
      } catch (error) {
        this.actionError = extractApiErrorMessage(error, 'Gagal memperbarui prioritas/jadwal')
      } finally {
        this.savingInfo = false
      }
    },
    async saveAgent() {
      if (this.savingAgent) return
      if (this.assignForm.assigned_agent_id === this.detail.assigned_agent_id) return
      this.savingAgent = true
      this.clearMessages()
      try {
        const response = await this.$store.dispatch('ticket/assignAgent', {
          id: this.detail.id,
          assigned_agent_id: this.assignForm.assigned_agent_id
        })
        this.applyDetail(response.data)
        this.$emit('updated', response.data)
        this.showSuccess('PIC tiket berhasil diperbarui.')
      } catch (error) {
        this.actionError = extractApiErrorMessage(error, 'Gagal mengubah PIC tiket')
        this.assignForm.assigned_agent_id = this.detail.assigned_agent_id || ''
      } finally {
        this.savingAgent = false
      }
    },
    async saveNote() {
      if (this.savingNote) return
      if (!this.noteForm.note) return
      this.savingNote = true
      this.clearMessages()
      try {
        const response = await this.$store.dispatch('ticket/addNote', {
          id: this.detail.id,
          note: this.noteForm.note
        })
        this.applyDetail(response.data)
        this.$emit('updated', response.data)
        this.noteForm.note = ''
        this.showSuccess('Catatan internal berhasil ditambahkan.')
      } catch (error) {
        this.actionError = extractApiErrorMessage(error, 'Gagal menambahkan catatan')
      } finally {
        this.savingNote = false
      }
    },
    async openChat() {
      if (this.chatOpening || !this.detail) return
      this.chatOpening = true
      this.clearMessages()
      try {
        await openChatRoomForSession(this.$store, {
          message_number: this.detail.contact_number,
          license_id: this.detail.license_id,
          license_phone_number: this.detail.license_phone_number,
          room_id: this.detail.room_id
        })
      } catch (error) {
        this.actionError = error && error.message ? error.message : 'Gagal membuka Chat Room.'
      } finally {
        this.chatOpening = false
      }
    },
    clearMessages() {
      this.actionError = ''
      this.actionSuccess = ''
    },
    showSuccess(message) {
      this.actionSuccess = message
      this.$fire({
        title: 'Berhasil',
        text: message,
        type: 'success',
        timer: 2400
      })
    }
  }
}
</script>

<style scoped>
.waajo-ticket-detail-panel {
  width: 100%;
  min-width: 0;
}
.waajo-ticket-detail-page {
  padding-top: 22px;
}
.waajo-ticket-detail-close {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  border-radius: 50%;
  background: #fff;
  color: var(--waajo-text-muted);
  font-size: 16px;
  flex: 0 0 auto;
}
.waajo-ticket-detail-close:hover,
.waajo-ticket-detail-close:focus {
  border-color: var(--waajo-brand-border-strong);
  color: var(--waajo-brand-ink);
}
.waajo-ticket-back {
  border: 0;
  background: transparent;
  color: var(--waajo-text-muted);
  font-size: 14px;
  font-weight: 600;
  padding: 0;
  margin-bottom: 16px;
}
.waajo-ticket-detail-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  margin-bottom: 20px;
}
.waajo-ticket-detail-head-copy {
  min-width: 0;
}
.waajo-ticket-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: var(--waajo-brand);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
}
.waajo-ticket-detail-title {
  margin: 6px 0 9px;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--waajo-text);
  line-height: 1.25;
}
.waajo-ticket-detail-badges {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.waajo-ticket-detail-category {
  font-size: 12px;
  color: var(--waajo-text-muted);
  padding: 4px 9px;
  border: 1px solid var(--waajo-border);
  border-radius: 999px;
  background: #fff;
}
.waajo-ticket-detail-head-actions {
  display: flex;
  gap: 8px;
}
.waajo-ticket-detail-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 18px;
  align-items: start;
}
.waajo-ticket-detail-main,
.waajo-ticket-detail-side {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.waajo-ticket-panel {
  border: 1px solid var(--waajo-border);
  border-radius: 14px;
  background: #fff;
  padding: 18px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
}
.waajo-ticket-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 15px;
}
.waajo-ticket-panel-head h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--waajo-text);
}
.waajo-ticket-panel-head h4 i {
  color: var(--waajo-brand);
  margin-right: 7px;
}
.waajo-ticket-panel-count {
  font-size: 12px;
  color: var(--waajo-text-muted);
}
.waajo-ticket-link-btn {
  border: 0;
  background: transparent;
  color: var(--waajo-brand-ink);
  font-size: 13px;
  font-weight: 700;
  padding: 0;
}
.waajo-ticket-description {
  margin: 0 0 18px;
  font-size: 14px;
  line-height: 1.7;
  color: var(--waajo-text-body);
  white-space: pre-wrap;
}
.waajo-ticket-meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 20px;
  padding-top: 15px;
  border-top: 1px solid #f1f5f9;
}
.waajo-ticket-meta-grid span,
.waajo-ticket-info-list span {
  display: block;
  font-size: 12px;
  color: var(--waajo-text-faint);
  text-transform: uppercase;
  letter-spacing: .04em;
  margin-bottom: 4px;
}
.waajo-ticket-meta-grid strong,
.waajo-ticket-info-list strong {
  display: block;
  font-size: 14px;
  color: var(--waajo-text);
  line-height: 1.45;
}
.waajo-ticket-info-list {
  display: flex;
  flex-direction: column;
  gap: 13px;
  margin: 16px 0;
}
.waajo-ticket-info-list small {
  display: block;
  font-size: 12px;
  color: var(--waajo-text-muted);
  margin-top: 3px;
}
.waajo-ticket-customer-card {
  display: flex;
  align-items: center;
  gap: 11px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f1f5f9;
}
.waajo-ticket-customer-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--waajo-brand-soft);
  color: var(--waajo-brand-ink);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
}
.waajo-ticket-customer-card strong {
  display: block;
  font-size: 14px;
  color: var(--waajo-text);
}
.waajo-ticket-customer-card span {
  display: block;
  font-size: 12px;
  color: var(--waajo-text-muted);
  margin-top: 3px;
}
.waajo-ticket-master-badge {
  display: inline-block !important;
  margin-top: 5px !important;
  padding: 2px 7px;
  border-radius: 999px;
  background: var(--waajo-brand-soft);
  color: var(--waajo-brand-ink) !important;
  font-size: 10px !important;
  font-weight: 700;
}
.waajo-ticket-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 9px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.5;
}
.waajo-ticket-badge.is-status-open { background: var(--waajo-status-open-bg); color: var(--waajo-status-open-ink); border-color: var(--waajo-status-open-border); }
.waajo-ticket-badge.is-status-in-progress { background: var(--waajo-status-progress-bg); color: var(--waajo-status-progress-ink); border-color: var(--waajo-status-progress-border); }
.waajo-ticket-badge.is-status-resolved { background: var(--waajo-status-resolved-bg); color: var(--waajo-status-resolved-ink); border-color: var(--waajo-status-resolved-border); }
.waajo-ticket-badge.is-priority-urgent { background: var(--waajo-priority-urgent-bg); color: var(--waajo-priority-urgent-ink); border-color: var(--waajo-priority-urgent-border); }
.waajo-ticket-badge.is-priority-normal { background: var(--waajo-priority-normal-bg); color: var(--waajo-priority-normal-ink); border-color: var(--waajo-priority-normal-border); }
.waajo-ticket-badge.is-priority-low { background: var(--waajo-priority-low-bg); color: var(--waajo-priority-low-ink); border-color: var(--waajo-priority-low-border); }
.waajo-ticket-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: var(--waajo-text-body);
  border-radius: 9999px;
  min-height: 38px;
  padding: 0 15px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
}
.waajo-ticket-btn.is-primary {
  border-color: var(--waajo-brand);
  background: var(--waajo-brand);
  color: #fff;
  font-weight: 700;
  box-shadow: 0 7px 16px rgba(243, 156, 18, 0.24);
}
.waajo-ticket-btn.is-primary:hover,
.waajo-ticket-btn.is-primary:focus {
  border-color: var(--waajo-brand-strong);
  background: var(--waajo-brand-strong);
}
.waajo-ticket-btn.is-block {
  display: flex;
  width: 100%;
  margin-top: 12px;
}
.waajo-ticket-btn:disabled {
  opacity: .6;
}
.waajo-ticket-action-block {
  padding: 14px 0;
  border-top: 1px solid #f1f5f9;
}
.waajo-ticket-action-block:first-of-type {
  padding-top: 0;
  border-top: 0;
}
.waajo-ticket-action-block label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--waajo-text-body);
  margin-bottom: 7px;
}
.waajo-ticket-action-block .waajo-ticket-action-label {
  margin-top: 10px;
}
.waajo-ticket-action-block select,
.waajo-ticket-action-block input,
.waajo-ticket-action-block textarea,
.waajo-ticket-edit-form input,
.waajo-ticket-edit-form select,
.waajo-ticket-edit-form textarea {
  width: 100%;
  min-height: 40px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 9px 11px;
  font-size: 14px;
  line-height: 1.45;
  color: var(--waajo-text);
  background: #fff;
  outline: none;
}
.waajo-ticket-action-block select:focus,
.waajo-ticket-action-block input:focus,
.waajo-ticket-action-block textarea:focus,
.waajo-ticket-edit-form input:focus,
.waajo-ticket-edit-form select:focus,
.waajo-ticket-edit-form textarea:focus {
  border-color: var(--waajo-brand-border-strong);
  box-shadow: 0 0 0 3px var(--waajo-focus-ring);
}
.waajo-ticket-action-block textarea,
.waajo-ticket-edit-form textarea {
  resize: vertical;
}
.waajo-ticket-action-block small {
  display: block;
  font-size: 12px;
  color: var(--waajo-text-faint);
  margin-top: 6px;
}
.waajo-ticket-inline-form {
  display: flex;
  gap: 8px;
}
.waajo-ticket-inline-form select {
  flex: 1;
}
.waajo-ticket-inline-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}
.waajo-ticket-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.waajo-ticket-form-group {
  margin-bottom: 13px;
}
.waajo-ticket-form-group label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--waajo-text-body);
  margin-bottom: 7px;
}
.waajo-ticket-form-error,
.waajo-ticket-form-success {
  padding: 11px 13px;
  margin-bottom: 16px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.5;
}
.waajo-ticket-form-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
}
.waajo-ticket-form-success {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #15803d;
}
.waajo-ticket-timeline {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.waajo-ticket-timeline-item {
  display: flex;
  gap: 12px;
  position: relative;
  padding-bottom: 19px;
}
.waajo-ticket-timeline-item:not(:last-child)::before {
  content: '';
  position: absolute;
  left: 16px;
  top: 33px;
  bottom: 2px;
  width: 1px;
  background: #e2e8f0;
}
.waajo-ticket-timeline-icon {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  color: #475569;
  font-size: 13px;
  z-index: 1;
}
.waajo-ticket-timeline-icon.is-ticket_created { background: var(--waajo-brand-soft); color: var(--waajo-brand-ink); }
.waajo-ticket-timeline-icon.is-ticket_resolved { background: var(--waajo-status-resolved-bg); color: var(--waajo-status-resolved-ink); }
.waajo-ticket-timeline-icon.is-ticket_reopened { background: var(--waajo-status-progress-bg); color: var(--waajo-status-progress-ink); }
.waajo-ticket-timeline-icon.is-internal_note { background: var(--waajo-brand-soft); color: var(--waajo-brand-ink); }
.waajo-ticket-timeline-icon.is-agent_assigned { background: #f5f3ff; color: #7c3aed; }
.waajo-ticket-timeline-icon.is-priority_changed { background: var(--waajo-priority-urgent-bg); color: var(--waajo-priority-urgent-ink); }
.waajo-ticket-timeline-icon.is-contact_selected { background: var(--waajo-brand-soft); color: var(--waajo-brand-ink); }
.waajo-ticket-timeline-icon.is-license_changed { background: var(--waajo-status-open-bg); color: var(--waajo-status-open-ink); }
.waajo-ticket-timeline-icon.is-auto_follow_up_scheduled { background: var(--waajo-brand-soft); color: var(--waajo-brand-ink); }
.waajo-ticket-timeline-icon.is-auto_follow_up_cancelled,
.waajo-ticket-timeline-icon.is-auto_follow_up_cancel_failed { background: #fef2f2; color: #b91c1c; }
.waajo-ticket-timeline-icon.is-auto_follow_up_completed { background: var(--waajo-status-resolved-bg); color: var(--waajo-status-resolved-ink); }
.waajo-ticket-timeline-icon.is-auto_follow_up_stopped { background: var(--waajo-status-progress-bg); color: var(--waajo-status-progress-ink); }
.waajo-ticket-timeline-icon.is-auto_follow_up_skipped { background: #fffbeb; color: #92400e; }
.waajo-ticket-timeline-icon.is-auto_follow_up_failed { background: #fef2f2; color: #b91c1c; }
.waajo-ticket-timeline-body {
  min-width: 0;
  padding-top: 1px;
}
.waajo-ticket-timeline-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
}
.waajo-ticket-timeline-top strong {
  font-size: 14px;
  color: var(--waajo-text);
}
.waajo-ticket-timeline-top small {
  font-size: 12px;
  color: var(--waajo-text-faint);
  white-space: nowrap;
}
.waajo-ticket-timeline-body p {
  margin: 4px 0 5px;
  font-size: 14px;
  color: var(--waajo-text-body);
  line-height: 1.55;
  white-space: pre-wrap;
}
.waajo-ticket-timeline-body p.is-note {
  padding: 10px 12px;
  background: var(--waajo-brand-soft-2);
  border: 1px solid var(--waajo-brand-border);
  border-radius: 8px;
  color: var(--waajo-brand-ink-strong);
}
.waajo-ticket-timeline-actor {
  font-size: 12px;
  color: var(--waajo-text-faint);
}
.waajo-ticket-timeline-empty {
  padding: 22px 0;
  text-align: center;
  color: var(--waajo-text-faint);
  font-size: 13px;
}
.waajo-ticket-state {
  padding: 50px 20px;
  text-align: center;
  color: var(--waajo-text-muted);
}
.waajo-ticket-state i {
  display: block;
  font-size: 34px;
  color: #cbd5e1;
  margin-bottom: 10px;
}
.waajo-ticket-state p {
  margin: 0 0 14px;
  font-size: 14px;
}
.waajo-ticket-state.is-error {
  color: #b42318;
}
.waajo-ticket-state.is-error i {
  color: #fca5a5;
}
.waajo-ticket-detail-loading {
  padding: 20px 0;
}
.waajo-ticket-sk {
  display: block;
  width: 70%;
  height: 14px;
  border-radius: 5px;
  margin-bottom: 10px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: waajoTicketSk 1.2s infinite;
}
.waajo-ticket-sk.is-title {
  width: 42%;
  height: 26px;
}
.waajo-ticket-sk.is-short {
  width: 30%;
}
@keyframes waajoTicketSk {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
@media (max-width: 992px) {
  .waajo-ticket-detail-grid {
    grid-template-columns: 1fr;
  }
  .waajo-ticket-detail-head {
    flex-direction: column;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/ticket/components/TicketDetailPanel.vue